// components/OwnerDoctorManagement.tsx
// Owner-only page: list doctors in the clinic, add new ones, assign services
// and insurance companies in bulk, sync prices, and view per-doctor services.
// Uses a secondary Firebase app so creating a new Auth user does NOT sign out
// the currently logged-in owner.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Card, Typography, Button, Form, Input, Modal, Table,
  Space, Tag, message, Spin, Divider, Row, Col, Avatar, Tooltip, Popconfirm,
  Checkbox, Alert, Badge, Empty, Drawer, Grid, Select,
} from 'antd';
import {
  PlusOutlined, UserOutlined, ArrowLeftOutlined,
  TeamOutlined, ReloadOutlined, LockOutlined, MailOutlined,
  EditOutlined, StopOutlined, UnlockOutlined, DeleteOutlined,
  ExclamationCircleOutlined, AppstoreAddOutlined, MedicineBoxOutlined,
  ScheduleOutlined, SafetyCertificateOutlined, SyncOutlined,
  EyeOutlined, KeyOutlined, LinkOutlined, UserAddOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  initializeApp, getApps,
} from 'firebase/app';
import {
  getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as firebaseSignOut,
} from 'firebase/auth';
import {
  collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, query, where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { useLanguage } from './LanguageContext';
import { auth, db } from '../firebaseConfig';
import API from '../config/api';
import { ClinicService } from './type';
import './OwnerDoctorManagement.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const assistantPrivilegesCopy = {
  ar: {
    button: 'الصلاحيات',
    tooltipTitle: 'إدارة صلاحيات المساعد',
    tooltipDescription: 'إظهار أو إخفاء زر «تحصيلاتي اليوم» للمساعد.',
    modalTitle: 'صلاحيات المساعد',
    modalDescription: 'اختر ما يمكن لهذا المساعد الوصول إليه في بوابة المساعد داخل هذه العيادة.',
    dailyCollections: 'إظهار «تحصيلاتي اليوم»',
    dailyCollectionsDescription: 'السماح للمساعد بفتح تقرير تحصيلاته اليومية. عند إيقافها يختفي الزر ويتم منع الوصول من الـAPI أيضًا.',
    visible: 'تحصيلاتي اليوم ظاهرة',
    hidden: 'تحصيلاتي اليوم مخفية',
    save: 'حفظ الصلاحيات',
    cancel: 'إلغاء',
    saved: 'تم تحديث صلاحيات المساعد',
    saveError: 'تعذر تحديث صلاحيات المساعد.',
  },
  en: {
    button: 'Privileges',
    tooltipTitle: 'Manage assistant privileges',
    tooltipDescription: 'Show or hide “My collections today” for this assistant.',
    modalTitle: 'Assistant Privileges',
    modalDescription: 'Choose what this assistant can access in the Assistant Portal for this clinic.',
    dailyCollections: 'Show “My collections today”',
    dailyCollectionsDescription: "Allow access to the assistant's daily collections report. Turning this off hides the button and blocks API access.",
    visible: 'Daily collections visible',
    hidden: 'Daily collections hidden',
    save: 'Save privileges',
    cancel: 'Cancel',
    saved: 'Assistant privileges updated',
    saveError: 'Failed to update assistant privileges.',
  },
} as const;

const doctorActionHintsCopy = {
  ar: {
    editTitle: 'تعديل بيانات الطبيب',
    editDescription: 'تعديل اسم الطبيب والتخصص.',
    servicesTitle: 'خدمات الطبيب',
    servicesDescription: 'عرض الخدمات المرتبطة بهذا الطبيب وإدارتها.',
    privilegesTitle: 'صلاحيات الطبيب',
    privilegesDescription: 'تحديد الصفحات والبيانات التي يمكن للطبيب الوصول إليها في لوحة التحكم.',
    passwordTitle: 'تغيير كلمة المرور',
    passwordDescription: 'تعيين كلمة مرور جديدة لحساب الطبيب.',
    noEmailTitle: 'لا يوجد حساب دخول',
    noEmailDescription: 'يجب ربط بريد إلكتروني بالطبيب أولًا قبل تغيير كلمة المرور.',
    linkTitle: 'ربط حساب دخول',
    linkDescription: 'ربط حساب Firebase موجود لتفعيل دخول الطبيب إلى لوحة التحكم.',
    enableTitle: 'تفعيل تسجيل الدخول',
    enableDescription: 'السماح للطبيب بتسجيل الدخول إلى لوحة التحكم مرة أخرى.',
    disableTitle: 'إيقاف تسجيل الدخول',
    disableDescription: 'منع الطبيب من تسجيل الدخول مؤقتًا دون حذف بياناته.',
    deleteTitle: 'حذف الطبيب نهائيًا',
    deleteDescription: 'حذف حساب الدخول وملف الطبيب وروابطه بهذه العيادة. هذا الإجراء لا يمكن التراجع عنه.',
  },
  en: {
    editTitle: 'Edit doctor details',
    editDescription: "Change the doctor's name and specialization.",
    servicesTitle: "Doctor's services",
    servicesDescription: 'View and manage services assigned to this doctor.',
    privilegesTitle: 'Doctor privileges',
    privilegesDescription: 'Choose which dashboard pages and data this doctor can access.',
    passwordTitle: 'Change password',
    passwordDescription: "Set a new password for the doctor's account.",
    noEmailTitle: 'No login account',
    noEmailDescription: 'Link an email account before changing the password.',
    linkTitle: 'Link login account',
    linkDescription: 'Link an existing Firebase account to enable dashboard login.',
    enableTitle: 'Enable dashboard login',
    enableDescription: 'Allow this doctor to sign in to the dashboard again.',
    disableTitle: 'Disable dashboard login',
    disableDescription: 'Temporarily block dashboard login without deleting doctor data.',
    deleteTitle: 'Delete doctor permanently',
    deleteDescription: 'Delete the login, doctor profile, and clinic links. This cannot be undone.',
  },
} as const;

// ── Secondary Firebase app (same config) so creating a new user
//    doesn't sign out the owner from the primary app. ─────────────
const firebaseConfig = {
  apiKey: 'AIzaSyDJ8HyRAm7z0RlgmYy3EjubVujy7E3ZBfA',
  authDomain: 'drwaiting-30f56.firebaseapp.com',
  projectId: 'drwaiting-30f56',
  storageBucket: 'drwaiting-30f56.firebasestorage.app',
  messagingSenderId: '937005545176',
  appId: '1:937005545176:web:be56ec33fce7e64e9f49ad',
};

const SECONDARY_APP_NAME = 'owner-doctor-create';
const getSecondaryApp = () => {
  const existing = getApps().find(a => a.name === SECONDARY_APP_NAME);
  return existing ?? initializeApp(firebaseConfig, SECONDARY_APP_NAME);
};

/** Firebase UID of whoever is logged in on the dashboard (the clinic owner). Same value used as assistant_id in admin portal rows like haithamsaad@gmail.com → all doctors at that clinic. */
function getClinicOwnerAssistantUid(): string {
  const fromAuth = auth.currentUser?.uid?.trim() || '';
  if (fromAuth) return fromAuth;
  return (localStorage.getItem('assistantId') || '').trim();
}

/** Key for (assistant_id, doctor_id) — clinic is fixed in each flow. */
function assistantDoctorKey(assistantId: string, doctorId: string): string {
  return `${assistantId}|${doctorId}`;
}

function assistantDoctorPairKeys(
  dcaDocs: { data: () => Record<string, unknown> }[],
): Set<string> {
  const s = new Set<string>();
  dcaDocs.forEach(d => {
    const x = d.data();
    const aid = x.assistant_id as string | undefined;
    const did = x.doctor_id as string | undefined;
    if (aid && did) s.add(assistantDoctorKey(aid, did));
  });
  return s;
}

// ── Types ──────────────────────────────────────────────────────────
interface DoctorRecord {
  id: string;
  name: string;
  specialty_id?: string;
  specialization?: string;
  email?: string;
  is_owner?: boolean;
  auth_uid?: string;
  disabled?: boolean;
  privileges?: {
    showRevenue?: boolean;
    viewDoctorAttendance?: boolean;
    manageInsurance?: boolean;
    viewerOnly?: boolean;
  };
}

interface SpecialtyOption {
  id: string;
  name_ar?: string;
  name_en?: string;
  isActive?: boolean;
}

const LEGACY_SPECIALTY_PREFIX = '__legacy_specialty__:';

const isLegacyCompatibleOwner = (doctor: DoctorRecord | null | undefined): boolean =>
  doctor != null && (doctor.is_owner === true || doctor.is_owner === undefined);

type DoctorPrivilegeKey = 'showRevenue' | 'viewDoctorAttendance' | 'manageInsurance' | 'viewerOnly';

const DOCTOR_PRIVILEGES: Array<{
  key: DoctorPrivilegeKey;
  label: string;
  description: string;
}> = [
  {
    key: 'showRevenue',
    label: 'Show revenue',
    description: 'Allow this doctor to see the revenue pill and revenue/expenses pages.',
  },
  {
    key: 'viewDoctorAttendance',
    label: 'View doctor attendance',
    description: 'Allow this doctor to view the attendance report for all doctors in the clinic.',
  },
  {
    key: 'manageInsurance',
    label: 'Insurance access',
    description: 'Allow this doctor to manage insurance companies and view/export insurance reports.',
  },
  {
    key: 'viewerOnly',
    label: 'Viewer only',
    description: 'Allow this doctor to view only their own data, with all add, edit, and delete actions blocked.',
  },
];

interface AssistantRecord {
  id: string;
  name: string;
  email?: string;
  disabled?: boolean;
  canViewDailyRevenue: boolean;
}

interface CustomVisitType {
  type_id: string;
  name: string;
  name_ar: string;
  normal_price: number;
  urgent_price: number;
  is_active: boolean;
  order: number;
}

interface InsuranceCompanyRecord {
  company_id: string;
  doctor_id: string;
  name_ar: string;
  name_en: string;
  coverage_percentage: number;
  notes: string;
  isActive: boolean;
}

// ── Component ──────────────────────────────────────────────────────
const OwnerDoctorManagement: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const assistantPrivilegesText = assistantPrivilegesCopy[language];
  const doctorActionHints = doctorActionHintsCopy[language];
  const renderActionHint = (title: string, description: string) => (
    <div>
      <strong>{title}</strong>
      <div>{description}</div>
    </div>
  );
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [doctors, setDoctors] = useState<DoctorRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [addAssistantModalVisible, setAddAssistantModalVisible] = useState(false);
  const [addAssistantSubmitting, setAddAssistantSubmitting] = useState(false);
  const [assistantForm] = Form.useForm();
  const [assistants, setAssistants] = useState<AssistantRecord[]>([]);
  const [assistantsLoading, setAssistantsLoading] = useState(false);
  const [assistantActionLoading, setAssistantActionLoading] = useState<string | null>(null);
  const [editingAssistant, setEditingAssistant] = useState<AssistantRecord | null>(null);
  const [editAssistantModalVisible, setEditAssistantModalVisible] = useState(false);
  const [editAssistantSubmitting, setEditAssistantSubmitting] = useState(false);
  const [editAssistantForm] = Form.useForm();
  const [assistantPrivilegesModalVisible, setAssistantPrivilegesModalVisible] = useState(false);
  const [privilegesAssistant, setPrivilegesAssistant] = useState<AssistantRecord | null>(null);
  const [assistantCanViewDailyRevenue, setAssistantCanViewDailyRevenue] = useState(false);
  const [assistantPrivilegesSubmitting, setAssistantPrivilegesSubmitting] = useState(false);
  const [passwordAssistant, setPasswordAssistant] = useState<AssistantRecord | null>(null);
  const [assistantPasswordModalVisible, setAssistantPasswordModalVisible] = useState(false);
  const [assistantPasswordSubmitting, setAssistantPasswordSubmitting] = useState(false);
  const [assistantPasswordForm] = Form.useForm();

  // ── Edit modal ────────────────────────────────────────────────────
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<DoctorRecord | null>(null);
  const [editForm] = Form.useForm();
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
  const [specialtiesLoading, setSpecialtiesLoading] = useState(false);

  // ── Password change ───────────────────────────────────────────────
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [passwordDoctor, setPasswordDoctor] = useState<DoctorRecord | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [changePasswordLoadingDoctorId, setChangePasswordLoadingDoctorId] = useState<string | null>(null);
  const [passwordForm] = Form.useForm();

  // ── Per-row action loading ────────────────────────────────────────
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [privilegesModalVisible, setPrivilegesModalVisible] = useState(false);
  const [privilegesDoctor, setPrivilegesDoctor] = useState<DoctorRecord | null>(null);
  const [selectedPrivilegeKeys, setSelectedPrivilegeKeys] = useState<DoctorPrivilegeKey[]>([]);
  const [privilegesSubmitting, setPrivilegesSubmitting] = useState(false);

  // ── Assign services / visit-types modal ──────────────────────────
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [ownerServices, setOwnerServices] = useState<ClinicService[]>([]);
  const [ownerVisitTypes, setOwnerVisitTypes] = useState<CustomVisitType[]>([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedVisitTypeIds, setSelectedVisitTypeIds] = useState<string[]>([]);

  // ── Assign insurance companies modal ─────────────────────────────
  const [assignInsuranceModalVisible, setAssignInsuranceModalVisible] = useState(false);
  const [assignInsuranceLoading, setAssignInsuranceLoading] = useState(false);
  const [assignInsuranceSubmitting, setAssignInsuranceSubmitting] = useState(false);
  const [ownerInsurance, setOwnerInsurance] = useState<InsuranceCompanyRecord[]>([]);
  const [selectedInsuranceDoctorIds, setSelectedInsuranceDoctorIds] = useState<string[]>([]);
  const [selectedInsuranceIds, setSelectedInsuranceIds] = useState<string[]>([]);

  // ── Sync prices ───────────────────────────────────────────────────
  const [syncPricesLoading, setSyncPricesLoading] = useState(false);

  // ── View doctor services modal ────────────────────────────────────
  const [doctorServicesModalVisible, setDoctorServicesModalVisible] = useState(false);
  const [viewingDoctor, setViewingDoctor] = useState<DoctorRecord | null>(null);
  const [doctorServicesList, setDoctorServicesList] = useState<ClinicService[]>([]);
  const [doctorServicesLoading, setDoctorServicesLoading] = useState(false);
  const [serviceActionLoadingId, setServiceActionLoadingId] = useState<string | null>(null);

  // ── Link existing login modal ─────────────────────────────────────
  const [linkLoginModalVisible, setLinkLoginModalVisible] = useState(false);
  const [linkLoginDoctor, setLinkLoginDoctor] = useState<DoctorRecord | null>(null);
  const [linkLoginSubmitting, setLinkLoginSubmitting] = useState(false);
  const [linkLoginForm] = Form.useForm();

  const clinicId = localStorage.getItem('clinicId') || '';
  const ownerDoctorId = localStorage.getItem('doctorId') || '';

  // ── Load doctors in this clinic from Firestore ─────────────────
  const loadDoctors = useCallback(async () => {
    if (!clinicId) {
      console.warn('[DrWaiting/Dashboard/ManageDoctors] loadDoctors aborted: no clinicId in localStorage');
      return;
    }
    setLoading(true);
    try {
      // Single source of truth: DCA collection — query all rows for this clinic,
      // deduplicate doctor_ids (each doctor has ≥2 rows: own login + owner row),
      // then fetch doctor profiles in one whereIn round-trip.
      const dcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const doctorIds: string[] = Array.from(
        new Set(dcaSnap.docs.map(d => d.data().doctor_id as string).filter(Boolean))
      );

      if (doctorIds.length === 0) {
        setDoctors([]);
        console.warn('[DrWaiting/Dashboard/ManageDoctors] No DCA rows for clinic', { clinicId });
        return;
      }

      // Firestore whereIn limit is 30; batch if needed (edge case: very large clinics)
      const BATCH = 30;
      const fetched: DoctorRecord[] = [];
      for (let i = 0; i < doctorIds.length; i += BATCH) {
        const batch = doctorIds.slice(i, i + BATCH);
        const snap = await getDocs(
          query(collection(db, 'doctors'), where('doctor_id', 'in', batch))
        );
        snap.docs.forEach(d => fetched.push({ id: d.id, ...d.data() } as DoctorRecord));
      }

      setDoctors(fetched);
      console.log('[DrWaiting/Dashboard/ManageDoctors] loadDoctors (DCA source)', {
        clinicId,
        uniqueDoctorIdsFromDCA: doctorIds.length,
        doctorProfilesResolved: fetched.length,
      });
    } catch (err) {
      console.error('Error loading clinic doctors:', err);
      message.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  }, [clinicId, ownerDoctorId]);

  const loadAssistants = useCallback(async () => {
    if (!clinicId) return;
    setAssistantsLoading(true);
    try {
      const dcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const assistantIds = Array.from(new Set(
        dcaSnap.docs
          .map(item => (item.data().assistant_id as string | undefined) || '')
          .filter(id => Boolean(id) && id !== getClinicOwnerAssistantUid()),
      ));
      const profiles = await Promise.all(
        assistantIds.map(async id => {
          const profile = await getDoc(doc(db, 'assistants', id));
          if (!profile.exists()) return null;
          const assistantLinks = dcaSnap.docs.filter(
            item => String(item.data().assistant_id || '').trim() === id,
          );
          return {
            id,
            ...profile.data(),
            // Preserve existing behavior for legacy relationships. Once the
            // owner saves this assistant, every clinic link gets an explicit value.
            canViewDailyRevenue: assistantLinks.every(
              item => item.data().can_view_daily_revenue !== false,
            ),
          } as AssistantRecord;
        })
      );
      setAssistants(profiles.filter((item): item is AssistantRecord => item !== null));
    } catch (err) {
      console.error('Error loading clinic assistants:', err);
      message.error('Failed to load assistants');
    } finally {
      setAssistantsLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    loadDoctors();
    loadAssistants();
  }, [loadDoctors, loadAssistants]);

  useEffect(() => {
    let cancelled = false;

    const loadSpecialties = async () => {
      setSpecialtiesLoading(true);
      try {
        const snapshot = await getDocs(
          query(collection(db, 'specialties'), where('isActive', '==', true))
        );
        const data = snapshot.docs.map(item => ({
          id: item.id,
          ...item.data(),
        })) as SpecialtyOption[];
        data.sort((a, b) => (a.name_ar || a.name_en || '').localeCompare(
          b.name_ar || b.name_en || '',
          language === 'ar' ? 'ar' : 'en',
        ));
        if (!cancelled) setSpecialties(data);
      } catch (err) {
        console.error('Failed to load specialties:', err);
        if (!cancelled) {
          message.error(language === 'ar'
            ? 'تعذر تحميل قائمة التخصصات.'
            : 'Failed to load specialties list.');
        }
      } finally {
        if (!cancelled) setSpecialtiesLoading(false);
      }
    };

    loadSpecialties();
    return () => { cancelled = true; };
  }, [language]);

  // ── Add doctor ───────────────────────────────────────────────────
  // Writes two clinic links (doctor_clinic_assistant), same idea as admin portal:
  //   • Owner row: assistant_id = logged-in owner's Firebase UID (one parent — e.g. haithamsaad@gmail.com manages all clinic doctors).
  //   • Staff row: assistant_id = new doctor's Firebase UID (needed so they can still log into the web dashboard alone).
  const handleAddDoctor = async (values: any) => {
    const { name, specialization, email, password } = values;
    if (!clinicId) {
      message.error('Clinic ID not found. Please log out and log in again.');
      return;
    }

    const ownerAssistantUid = getClinicOwnerAssistantUid();
    if (!ownerAssistantUid) {
      message.error('Cannot resolve owner account. Please sign out and sign in again.');
      return;
    }

    setSubmitting(true);
    let uid = '';
    let doctorId = '';
    try {

      const secondaryApp = getSecondaryApp();
      const secondaryAuth = getAuth(secondaryApp);
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      } catch (authErr: any) {
        if (authErr.code === 'auth/email-already-in-use') {
          throw new Error('This email is already registered. Use a different email.');
        } else if (authErr.code === 'auth/weak-password') {
          throw new Error('Password must be at least 6 characters.');
        } else {
          throw new Error('Failed to create login account: ' + (authErr.message || authErr.code));
        }
      }
      uid = userCredential.user.uid;
      console.log('[AddDoctor] Auth created for doctor. uid:', uid);

      const doctorDoc: any = {
        name,
        specialization: specialization || '',
        email,
        is_owner: false,
        privileges: {
          showRevenue: false,
          viewDoctorAttendance: false,
          manageInsurance: false,
          viewerOnly: false,
        },
        arrived: false,
        disabled: false,
      };
      let docRef;
      try {
        docRef = await addDoc(collection(db, 'doctors'), doctorDoc);
      } catch (err: any) {
        throw new Error('Failed to create doctor record: ' + (err.message || err.code));
      }
      doctorId = docRef.id;
      console.log('[AddDoctor] doctors doc:', doctorId);

      try {
        await updateDoc(doc(db, 'doctors', doctorId), { doctor_id: doctorId });
      } catch (err: any) {
        throw new Error('Failed to set doctor_id on doctor record: ' + (err.message || err.code));
      }

      const clinicDcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const pairKeys = assistantDoctorPairKeys(clinicDcaSnap.docs);

      if (!pairKeys.has(assistantDoctorKey(uid, doctorId))) {
        await addDoc(collection(db, 'doctor_clinic_assistant'), {
          assistant_id: uid,
          doctor_id: doctorId,
          clinic_id: clinicId,
        });
        pairKeys.add(assistantDoctorKey(uid, doctorId));
        console.log('[AddDoctor] DCA staff login row created');
      }

      if (!pairKeys.has(assistantDoctorKey(ownerAssistantUid, doctorId))) {
        await addDoc(collection(db, 'doctor_clinic_assistant'), {
          assistant_id: ownerAssistantUid,
          doctor_id: doctorId,
          clinic_id: clinicId,
        });
        pairKeys.add(assistantDoctorKey(ownerAssistantUid, doctorId));
        console.log('[AddDoctor] DCA owner-parent row (assistant:', ownerAssistantUid, ')');
      }

      // Create a DCA row for every existing assistant already linked to this clinic,
      // so the new doctor immediately appears in all assistant portals.
      const existingAssistantIds = Array.from(new Set(
        clinicDcaSnap.docs
          .map(d => (d.data().assistant_id as string | undefined) || '')
          .filter(aid => aid && aid !== uid && aid !== ownerAssistantUid),
      ));
      const assistantRevenuePermissions = new Map<string, boolean>();
      clinicDcaSnap.docs.forEach(item => {
        const data = item.data();
        const assistantId = String(data.assistant_id || '').trim();
        if (!existingAssistantIds.includes(assistantId)) return;
        const existingValue = assistantRevenuePermissions.get(assistantId) ?? true;
        assistantRevenuePermissions.set(
          assistantId,
          existingValue && data.can_view_daily_revenue !== false,
        );
      });
      for (const existingAid of existingAssistantIds) {
        if (!pairKeys.has(assistantDoctorKey(existingAid, doctorId))) {
          await addDoc(collection(db, 'doctor_clinic_assistant'), {
            assistant_id: existingAid,
            doctor_id: doctorId,
            clinic_id: clinicId,
            can_view_daily_revenue: assistantRevenuePermissions.get(existingAid) !== false,
          });
          pairKeys.add(assistantDoctorKey(existingAid, doctorId));
          console.log('[AddDoctor] DCA row created for existing clinic assistant:', existingAid);
        }
      }

      try {
        await updateDoc(doc(db, 'doctors', doctorId), {
          auth_uid: uid,
          email,
          clinic_id: clinicId,
        });
      } catch (err: any) {
        throw new Error('Failed to update doctor with auth info: ' + (err.message || err.code));
      }

      try {
        await firebaseSignOut(secondaryAuth);
      } catch {
        console.warn('[AddDoctor] Secondary auth sign-out skipped');
      }

      message.success(`Doctor "${name}" added — linked under clinic owner (${email.split('@')[0]} can still log in).`);
      form.resetFields();
      setAddModalVisible(false);
      loadDoctors();

    } catch (err: any) {
      console.error('[AddDoctor] Error:', err);
      message.error(err.message || 'Failed to create doctor. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit doctor name/specialization ─────────────────────────────
  // Mirrors registration's assistant profile and relationship creation, then
  // links the new assistant to all doctors currently in the owner's clinic.
  const handleAddAssistant = async (values: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    canViewDailyRevenue?: boolean;
  }) => {
    if (!clinicId) {
      message.error('Clinic ID not found. Please log out and log in again.');
      return;
    }

    const name = values.name.trim();
    const email = values.email.trim().toLowerCase();
    setAddAssistantSubmitting(true);

    try {
      const secondaryApp = getSecondaryApp();
      const secondaryAuth = getAuth(secondaryApp);
      let assistantUid = '';

      try {
        const credential = await createUserWithEmailAndPassword(secondaryAuth, email, values.password);
        assistantUid = credential.user.uid.trim();
      } catch (authErr: any) {
        if (authErr.code === 'auth/email-already-in-use') {
          throw new Error('This assistant email is already registered.');
        }
        if (authErr.code === 'auth/weak-password') {
          throw new Error('Password must be at least 6 characters.');
        }
        throw new Error('Failed to create assistant login: ' + (authErr.message || authErr.code));
      }

      await setDoc(doc(db, 'assistants', assistantUid), { email, name });

      const clinicDcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const pairKeys = assistantDoctorPairKeys(clinicDcaSnap.docs);
      const clinicDoctorIds = Array.from(new Set(
        clinicDcaSnap.docs
          .map(item => (item.data().doctor_id as string | undefined) || '')
          .filter(Boolean),
      ));

      if (ownerDoctorId && !clinicDoctorIds.includes(ownerDoctorId)) {
        clinicDoctorIds.push(ownerDoctorId);
      }

      for (const doctorId of clinicDoctorIds) {
        if (!pairKeys.has(assistantDoctorKey(assistantUid, doctorId))) {
          await addDoc(collection(db, 'doctor_clinic_assistant'), {
            assistant_id: assistantUid,
            doctor_id: doctorId,
            clinic_id: clinicId,
            can_view_daily_revenue: values.canViewDailyRevenue === true,
          });
          pairKeys.add(assistantDoctorKey(assistantUid, doctorId));
        }
      }

      await firebaseSignOut(secondaryAuth).catch(() => {});

      message.success(
        `Assistant "${name}" created and linked to ${clinicDoctorIds.length} doctor${clinicDoctorIds.length === 1 ? '' : 's'}.`
      );
      assistantForm.resetFields();
      setAddAssistantModalVisible(false);
      loadAssistants();
    } catch (err: any) {
      console.error('[AddAssistant] Error:', err);
      message.error(err.message || 'Failed to create assistant. Please try again.');
    } finally {
      setAddAssistantSubmitting(false);
    }
  };

  const manageAssistant = async (assistant: AssistantRecord, action: string, values: Record<string, unknown> = {}) => {
    const functions = getFunctions();
    const manageAssistantFn = httpsCallable(functions, 'ownerManageAssistant');
    return manageAssistantFn({ action, assistantId: assistant.id, clinicId, ...values });
  };

  const updateAssistantDailyRevenuePermission = async (
    assistantId: string,
    canViewDailyRevenue: boolean,
  ) => {
    const relationships = await getDocs(
      query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
    );
    const assistantLinks = relationships.docs.filter(
      item => String(item.data().assistant_id || '').trim() === assistantId,
    );
    if (assistantLinks.length === 0) {
      throw new Error('No clinic relationships were found for this assistant.');
    }
    await Promise.all(
      assistantLinks.map(item => updateDoc(item.ref, {
        can_view_daily_revenue: canViewDailyRevenue,
      }))
    );
  };

  const handleEditAssistant = async (values: { name: string }) => {
    if (!editingAssistant) return;
    setEditAssistantSubmitting(true);
    try {
      await manageAssistant(editingAssistant, 'updateName', { name: values.name.trim() });
      message.success(`Assistant "${values.name.trim()}" updated.`);
      setEditAssistantModalVisible(false);
      setEditingAssistant(null);
      editAssistantForm.resetFields();
      loadAssistants();
    } catch (err: any) {
      message.error(err?.message || 'Failed to update assistant.');
    } finally {
      setEditAssistantSubmitting(false);
    }
  };

  const openAssistantPrivilegesModal = (assistant: AssistantRecord) => {
    setPrivilegesAssistant(assistant);
    setAssistantCanViewDailyRevenue(assistant.canViewDailyRevenue);
    setAssistantPrivilegesModalVisible(true);
  };

  const handleSaveAssistantPrivileges = async () => {
    if (!privilegesAssistant) return;
    setAssistantPrivilegesSubmitting(true);
    try {
      await updateAssistantDailyRevenuePermission(
        privilegesAssistant.id,
        assistantCanViewDailyRevenue,
      );
      message.success(`${assistantPrivilegesText.saved}: "${privilegesAssistant.name}".`);
      setAssistantPrivilegesModalVisible(false);
      setPrivilegesAssistant(null);
      await loadAssistants();
    } catch (err: any) {
      message.error(err?.message || assistantPrivilegesText.saveError);
    } finally {
      setAssistantPrivilegesSubmitting(false);
    }
  };

  const handleAssistantPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    if (!passwordAssistant) return;
    setAssistantPasswordSubmitting(true);
    setAssistantActionLoading(passwordAssistant.id);
    try {
      await manageAssistant(passwordAssistant, 'changePassword', { newPassword: values.newPassword });
      message.success(`Password updated for "${passwordAssistant.name}".`);
      setAssistantPasswordModalVisible(false);
      setPasswordAssistant(null);
      assistantPasswordForm.resetFields();
    } catch (err: any) {
      message.error(err?.message || 'Failed to change assistant password.');
    } finally {
      setAssistantPasswordSubmitting(false);
      setAssistantActionLoading(null);
    }
  };

  const handleToggleAssistantDisabled = async (assistant: AssistantRecord) => {
    setAssistantActionLoading(assistant.id);
    try {
      await manageAssistant(assistant, 'setDisabled', { disabled: !assistant.disabled });
      message.success(`Assistant "${assistant.name}" ${assistant.disabled ? 'enabled' : 'disabled'}.`);
      loadAssistants();
    } catch (err: any) {
      message.error(err?.message || 'Failed to update assistant login status.');
    } finally {
      setAssistantActionLoading(null);
    }
  };

  const handleDeleteAssistant = async (assistant: AssistantRecord) => {
    setAssistantActionLoading(assistant.id);
    try {
      const result: any = await manageAssistant(assistant, 'delete');
      const accountDeleted = result?.data?.accountDeleted === true;
      message.success(
        accountDeleted
          ? `Assistant "${assistant.name}" and their login were deleted.`
          : `Assistant "${assistant.name}" was removed from this clinic. Their shared account was kept.`
      );
      loadAssistants();
    } catch (err: any) {
      message.error(err?.message || 'Failed to remove assistant.');
    } finally {
      setAssistantActionLoading(null);
    }
  };

  const openEditModal = (doctor: DoctorRecord) => {
    const normalizedCurrent = (doctor.specialization || '').trim().toLocaleLowerCase();
    const matchingSpecialty = specialties.find(item =>
      item.id === doctor.specialty_id
      || (normalizedCurrent && [item.name_ar, item.name_en].some(
        name => (name || '').trim().toLocaleLowerCase() === normalizedCurrent,
      ))
    );
    const specialtyId = matchingSpecialty?.id
      || (doctor.specialization
        ? `${LEGACY_SPECIALTY_PREFIX}${doctor.specialization}`
        : undefined);

    setEditingDoctor(doctor);
    editForm.setFieldsValue({ name: doctor.name, specialtyId });
    setEditModalVisible(true);
  };

  const handleEditDoctor = async (values: { name: string; specialtyId?: string }) => {
    if (!editingDoctor) return;
    setEditSubmitting(true);
    try {
      const selectedSpecialty = specialties.find(item => item.id === values.specialtyId);
      const keepLegacySpecialty = values.specialtyId?.startsWith(LEGACY_SPECIALTY_PREFIX) === true;
      const specialization = keepLegacySpecialty
        ? (editingDoctor.specialization || '')
        : selectedSpecialty
          ? (selectedSpecialty.name_ar || selectedSpecialty.name_en || '').trim()
          : '';
      const specialtyId = keepLegacySpecialty
        ? (editingDoctor.specialty_id || null)
        : (selectedSpecialty?.id || null);

      await updateDoc(doc(db, 'doctors', editingDoctor.id), {
        name: values.name,
        specialty_id: specialtyId,
        specialization,
      });
      message.success('Doctor updated successfully');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingDoctor(null);
      loadDoctors();
    } catch (err) {
      message.error('Failed to update doctor');
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Change doctor password directly (owner action) ──────────────
  const openChangePasswordModal = (doctor: DoctorRecord) => {
    setPasswordDoctor(doctor);
    passwordForm.resetFields();
    setChangePasswordModalVisible(true);
  };

  const handleChangePasswordSubmit = async (values: { newPassword: string; confirmPassword: string }) => {
    if (!passwordDoctor) return;
    if (values.newPassword !== values.confirmPassword) {
      message.error('Password confirmation does not match.');
      return;
    }

    setPasswordSubmitting(true);
    setChangePasswordLoadingDoctorId(passwordDoctor.id);
    try {
      const functions = getFunctions();
      const changeDoctorPasswordFn = httpsCallable(functions, 'changeDoctorPassword');
      await changeDoctorPasswordFn({
        doctorId: passwordDoctor.id,
        newPassword: values.newPassword,
        clinicId,
      });
      message.success(`Password updated for "${passwordDoctor.name}"`);
      setChangePasswordModalVisible(false);
      passwordForm.resetFields();
      setPasswordDoctor(null);
    } catch (err: any) {
      const msg = err?.message || err?.details || 'Failed to change password';
      message.error(msg);
    } finally {
      setPasswordSubmitting(false);
      setChangePasswordLoadingDoctorId(null);
    }
  };

  // ── Disable / Enable login ───────────────────────────────────────
  const handleToggleDisable = async (doctor: DoctorRecord) => {
    const newDisabled = !doctor.disabled;
    setActionLoading(doctor.id);
    try {
      const functions = getFunctions();
      const setDoctorDisabled = httpsCallable(functions, 'ownerSetDoctorDisabled');
      await setDoctorDisabled({ doctorId: doctor.id, clinicId, disabled: newDisabled });
      message.success(
        newDisabled
          ? `"${doctor.name}" login disabled.`
          : `"${doctor.name}" login re-enabled.`
      );
      loadDoctors();
    } catch (err: any) {
      message.error(err?.message || 'Failed to update login status');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Link existing Firebase account to doctor ──────────────────────
  // Signs in via a secondary Firebase app so the owner stays logged in,
  // then writes only auth_uid on the doctor document. Nothing else is touched.
  const openPrivilegesModal = (doctor: DoctorRecord) => {
    setPrivilegesDoctor(doctor);
    setSelectedPrivilegeKeys(
      DOCTOR_PRIVILEGES
        .filter(privilege => doctor.privileges?.[privilege.key] === true)
        .map(privilege => privilege.key)
    );
    setPrivilegesModalVisible(true);
  };

  const handleSavePrivileges = async () => {
    if (!privilegesDoctor) return;
    setPrivilegesSubmitting(true);
    try {
      const isOwner = privilegesDoctor.id === ownerDoctorId
        || isLegacyCompatibleOwner(privilegesDoctor);
      const updates = DOCTOR_PRIVILEGES.reduce<Record<string, boolean>>((acc, privilege) => {
        acc[`privileges.${privilege.key}`] = privilege.key === 'viewerOnly' && isOwner
          ? false
          : selectedPrivilegeKeys.includes(privilege.key);
        return acc;
      }, {});
      await updateDoc(doc(db, 'doctors', privilegesDoctor.id), updates);
      message.success(`Privileges updated for "${privilegesDoctor.name}".`);
      setPrivilegesModalVisible(false);
      setPrivilegesDoctor(null);
      setSelectedPrivilegeKeys([]);
      loadDoctors();
    } catch (err) {
      console.error('Failed to update privileges:', err);
      message.error('Failed to update privileges');
    } finally {
      setPrivilegesSubmitting(false);
    }
  };

  const handleLinkExistingLogin = async (values: { email: string; password: string }) => {
    if (!linkLoginDoctor) return;
    setLinkLoginSubmitting(true);
    try {
      const secondaryApp = getSecondaryApp();
      const secondaryAuth = getAuth(secondaryApp);
      const credential = await signInWithEmailAndPassword(secondaryAuth, values.email, values.password);
      const uid = credential.user.uid.trim();

      await updateDoc(doc(db, 'doctors', linkLoginDoctor.id), { auth_uid: uid });

      await firebaseSignOut(secondaryAuth).catch(() => {});

      message.success(`Login linked for "${linkLoginDoctor.name}". They can now log in with this email.`);
      setLinkLoginModalVisible(false);
      linkLoginForm.resetFields();
      setLinkLoginDoctor(null);
      loadDoctors();
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        message.error('Wrong email or password. Please check and try again.');
      } else {
        message.error(err?.message || 'Failed to link login.');
      }
    } finally {
      setLinkLoginSubmitting(false);
    }
  };

  // ── Remove doctor (Firestore + Firebase Auth via Cloud Function) ─
  const handleRemoveFromClinic = async (doctor: DoctorRecord) => {
    if (!clinicId) {
      message.error('Clinic ID not found. Please log in again.');
      return;
    }
    setActionLoading(doctor.id);
    try {
      const functionsInst = getFunctions();
      const removeFn = httpsCallable(functionsInst, 'ownerRemoveDoctorFromClinic');
      await removeFn({ doctorId: doctor.id, clinicId });
      message.success(`"${doctor.name}" removed — login deleted and clinic / assistant links cleared.`);
      loadDoctors();
    } catch (err: any) {
      const detail = typeof err?.message === 'string' ? err.message : '';
      message.error(detail ? `Remove failed: ${detail}` : 'Failed to remove doctor.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Load owner services + visit types for assign dialog ─────────
  const loadOwnerCatalog = async () => {
    if (!ownerDoctorId) return;
    setAssignLoading(true);
    try {
      const [servicesRes, visitTypesRes, settingsRes] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(ownerDoctorId)}`, {
          params: { includeInactive: 'false' },
        }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(ownerDoctorId)}`),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(ownerDoctorId)}`).catch(() => null),
      ]);

      setOwnerServices(servicesRes.data?.data || []);

      const allTypes: CustomVisitType[] = visitTypesRes.data?.data?.visit_types || [];

      // Legacy fees the doctor configured via the Settings page — used as
      // fallback when the VisitTypeConfiguration rows have price 0.
      const legacySettings = settingsRes?.data?.settings ?? {};
      const legacyFallback: Record<string, { normal: number; urgent: number }> = {
        visit:        { normal: Number(legacySettings.consultationFee ?? 0), urgent: Number(legacySettings.urgentFee ?? 0) },
        revisit:      { normal: Number(legacySettings.revisitFee ?? 0),      urgent: Number(legacySettings.urgentFee ?? 0) },
        consultation: { normal: Number(legacySettings.estisharaFee ?? 0),    urgent: Number(legacySettings.urgentFee ?? 0) },
      };

      const mergedTypes = allTypes.map((t): CustomVisitType => {
        const fb = legacyFallback[t.type_id];
        if (!fb) return t;
        return {
          ...t,
          normal_price: Number(t.normal_price) > 0 ? t.normal_price : fb.normal,
          urgent_price: Number(t.urgent_price) > 0 ? t.urgent_price : fb.urgent,
        };
      });

      setOwnerVisitTypes(mergedTypes.filter(t => t.is_active));
    } catch {
      message.error('Failed to load services catalog');
    } finally {
      setAssignLoading(false);
    }
  };

  const openAssignModal = () => {
    setSelectedDoctorIds([]);
    setSelectedServiceIds([]);
    setSelectedVisitTypeIds([]);
    setAssignModalVisible(true);
    loadOwnerCatalog();
  };

  // ── Batch assign services + visit types ─────────────────────────
  // Uses a single batch API call for services (no N×M individual requests).
  const handleAssignSubmit = async () => {
    if (selectedDoctorIds.length === 0) {
      message.warning('يرجى اختيار طبيب واحد على الأقل.');
      return;
    }
    if (selectedServiceIds.length === 0 && selectedVisitTypeIds.length === 0) {
      message.warning('يرجى اختيار خدمة أو نوع زيارة واحد على الأقل.');
      return;
    }

    const servicesToAdd = ownerServices.filter(s => selectedServiceIds.includes(s.service_id));
    const visitTypesToAdd = ownerVisitTypes.filter(vt => selectedVisitTypeIds.includes(vt.type_id));
    const targetDoctors = doctors.filter(d => selectedDoctorIds.includes(d.id) && d.id !== ownerDoctorId);

    if (targetDoctors.length === 0) {
      message.warning('لم يتم العثور على أطباء صالحين.');
      return;
    }

    setAssignSubmitting(true);
    try {
      // ── Single batch request for all services × all doctors ──────
      if (servicesToAdd.length > 0) {
        const allServiceEntries = targetDoctors.flatMap(doctor =>
          servicesToAdd.map(svc => ({
            name: svc.name,
            description: svc.description || '',
            price: svc.price,
            category: svc.category || 'general',
            doctor_id: doctor.id,
          }))
        );
        await axios.post(`${API.BASE_URL}${API.ENDPOINTS.SERVICES_BATCH_ASSIGN}`, {
          services: allServiceEntries,
        });
      }

      // ── Visit types: one merge-request per doctor ────────────────
      // Track real outcomes so we don't show a misleading success toast when
      // every assignment was silently skipped or failed.
      let visitTypeAddedDoctors = 0;
      let visitTypeSkippedDoctors = 0;
      const visitTypeFailedDoctors: string[] = [];

      for (const doctor of targetDoctors) {
        if (visitTypesToAdd.length === 0) continue;
        try {
          const existingRes = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctor.id)}`);
          const respData = existingRes.data?.data;
          // When the API returns hardcoded defaults (no saved config yet) it
          // sets isDefault: true. We must NOT treat those as already-existing
          // rows or the merge will both (a) wipe the owner's intended types
          // and (b) persist the placeholder defaults back into MongoDB.
          const isDefaultsOnly = respData?.isDefault === true;
          const existingTypes: CustomVisitType[] = isDefaultsOnly
            ? []
            : (respData?.visit_types || []);

          // Match on (type_id, name, name_ar) — name alone collides with the
          // standard "Visit"/"Re-visit"/"Consultation" defaults.
          const keyOf = (t: CustomVisitType) =>
            `${(t.type_id || '').toLowerCase()}|${(t.name || '').toLowerCase()}|${(t.name_ar || '').toLowerCase()}`;
          const existingKeys = new Set(existingTypes.map(keyOf));

          const newTypes = visitTypesToAdd
            .filter(vt => !existingKeys.has(keyOf(vt)))
            .map((vt, idx) => ({
              type_id: vt.type_id || `custom_${Date.now()}_${idx}`,
              name: vt.name,
              name_ar: vt.name_ar,
              normal_price: vt.normal_price,
              urgent_price: vt.urgent_price,
              is_active: true,
              order: existingTypes.length + idx + 1,
            }));

          if (newTypes.length === 0) {
            visitTypeSkippedDoctors++;
            continue;
          }

          await axios.post(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctor.id)}`, {
            visit_types: [...existingTypes, ...newTypes],
            default_type: 'visit',
          });
          visitTypeAddedDoctors++;
        } catch (err) {
          console.error('[AssignVisitTypes] failed for doctor', doctor.id, err);
          visitTypeFailedDoctors.push(doctor.name || doctor.id);
        }
      }

      // ── Build an honest summary toast ──────────────────────────────
      const parts: string[] = [];
      if (servicesToAdd.length > 0) {
        parts.push(`الخدمات: تمت الإضافة لـ ${targetDoctors.length} دكتور`);
      }
      if (visitTypesToAdd.length > 0) {
        parts.push(
          `أنواع الزيارة: تمت الإضافة لـ ${visitTypeAddedDoctors} دكتور` +
          (visitTypeSkippedDoctors > 0 ? `, تم تخطي ${visitTypeSkippedDoctors} (موجودة بالفعل)` : '')
        );
      }
      if (visitTypeFailedDoctors.length > 0) {
        message.warning(
          `${parts.join(' • ')} • فشل: ${visitTypeFailedDoctors.join(', ')}`
        );
      } else if (parts.length > 0) {
        message.success(parts.join(' • '));
      } else {
        message.info('لم يتم تنفيذ أي تغييرات.');
      }
    } catch {
      message.error('حدث خطأ أثناء الإضافة. يرجى المحاولة مجدداً.');
    } finally {
      setAssignSubmitting(false);
      setAssignModalVisible(false);
    }
  };

  // ── Load owner insurance companies ───────────────────────────────
  const loadOwnerInsurance = async () => {
    if (!ownerDoctorId) return;
    setAssignInsuranceLoading(true);
    try {
      const res = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.INSURANCE_COMPANIES(ownerDoctorId)}?includeInactive=false`
      );
      setOwnerInsurance(res.data?.data || []);
    } catch {
      message.error('فشل تحميل شركات التأمين');
    } finally {
      setAssignInsuranceLoading(false);
    }
  };

  const openAssignInsuranceModal = () => {
    setSelectedInsuranceDoctorIds([]);
    setSelectedInsuranceIds([]);
    setAssignInsuranceModalVisible(true);
    loadOwnerInsurance();
  };

  // ── Batch assign insurance companies to selected doctors ─────────
  const handleAssignInsuranceSubmit = async () => {
    if (selectedInsuranceDoctorIds.length === 0) {
      message.warning('يرجى اختيار طبيب واحد على الأقل.');
      return;
    }
    if (selectedInsuranceIds.length === 0) {
      message.warning('يرجى اختيار شركة تأمين واحدة على الأقل.');
      return;
    }

    const companiesToAdd = ownerInsurance.filter(c => selectedInsuranceIds.includes(c.company_id));
    const targetDoctors = doctors.filter(
      d => selectedInsuranceDoctorIds.includes(d.id) && d.id !== ownerDoctorId
    );

    if (targetDoctors.length === 0) { message.warning('لم يتم العثور على أطباء صالحين.'); return; }

    setAssignInsuranceSubmitting(true);
    try {
      const allEntries = targetDoctors.flatMap(doctor =>
        companiesToAdd.map(comp => ({
          name_ar: comp.name_ar,
          name_en: comp.name_en || '',
          coverage_percentage: comp.coverage_percentage,
          notes: comp.notes || '',
          doctor_id: doctor.id,
        }))
      );
      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.INSURANCE_BATCH_ASSIGN}`, {
        companies: allEntries,
      });
      message.success(`تم إضافة شركات التأمين بنجاح لـ ${targetDoctors.length} دكتور.`);
    } catch {
      message.error('حدث خطأ أثناء الإضافة. يرجى المحاولة مجدداً.');
    } finally {
      setAssignInsuranceSubmitting(false);
      setAssignInsuranceModalVisible(false);
    }
  };

  // ── Sync owner service prices to all other doctors ───────────────
  const handleSyncPrices = async () => {
    const targetIds = doctors.filter(d => d.id !== ownerDoctorId).map(d => d.id);
    if (targetIds.length === 0) { message.warning('لا يوجد أطباء لمزامنة الأسعار معهم.'); return; }
    setSyncPricesLoading(true);
    try {
      const servicesRes = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(ownerDoctorId)}`,
        { params: { includeInactive: 'false' } }
      );
      const ownerSvcs: ClinicService[] = servicesRes.data?.data || [];
      if (ownerSvcs.length === 0) { message.warning('لا توجد خدمات في حسابك لمزامنتها.'); return; }

      await axios.put(`${API.BASE_URL}${API.ENDPOINTS.SERVICES_SYNC_PRICES}`, {
        target_doctor_ids: targetIds,
        updates: ownerSvcs.map(s => ({ name: s.name, price: s.price })),
      });
      message.success('تم تزامن الأسعار بنجاح مع جميع الأطباء.');
    } catch {
      message.error('حدث خطأ أثناء تزامن الأسعار.');
    } finally {
      setSyncPricesLoading(false);
    }
  };

  // ── View a specific doctor's services ────────────────────────────
  const openDoctorServicesModal = async (doctor: DoctorRecord) => {
    setViewingDoctor(doctor);
    setDoctorServicesList([]);
    setDoctorServicesLoading(true);
    setDoctorServicesModalVisible(true);
    try {
      const res = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctor.id)}`,
        { params: { includeInactive: 'false' } }
      );
      // The doctor-facing list should behave as if a hidden service was deleted.
      // Keep a defensive client filter in case an older backend ignores the flag.
      setDoctorServicesList((res.data?.data || []).filter((service: ClinicService) => service.isActive !== false));
    } catch {
      message.error('فشل تحميل خدمات الدكتور.');
    } finally {
      setDoctorServicesLoading(false);
    }
  };

  const handleSetDoctorServiceActive = async (service: ClinicService, isActive: boolean) => {
    if (!viewingDoctor) return;
    setServiceActionLoadingId(service.service_id);
    try {
      if (isActive) {
        await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.SERVICE(viewingDoctor.id, service.service_id)}`,
          { isActive: true }
        );
      } else {
        await axios.delete(
          `${API.BASE_URL}${API.ENDPOINTS.SERVICE(viewingDoctor.id, service.service_id)}`
        );
      }
      setDoctorServicesList(prev => isActive
        ? prev.map(item => item.service_id === service.service_id ? { ...item, isActive: true } : item)
        : prev.filter(item => item.service_id !== service.service_id));
      message.success(isActive
        ? `Service "${service.name}" restored for ${viewingDoctor.name}`
        : `Service "${service.name}" hidden; old receipts remain unchanged`);
    } catch {
      message.error('Failed to change service status for this doctor');
    } finally {
      setServiceActionLoadingId(null);
    }
  };

  // ── Table columns ────────────────────────────────────────────────
  const renderDoctorStatus = (record: DoctorRecord) => (
    <Space size={[4, 4]} wrap>
      {record.is_owner === true && <Tag color="purple">Owner</Tag>}
      {record.is_owner === false && <Tag color="blue">Doctor</Tag>}
      {record.is_owner === undefined && <Tag color="default">Legacy</Tag>}
      {record.is_owner === true ? (
        <Tooltip title="This is the owner doctor's own revenue, not the combined revenue of all doctors.">
          <Tag color="green">Owner revenue only</Tag>
        </Tooltip>
      ) : record.privileges?.showRevenue === true ? (
        <Tag color="green">Revenue</Tag>
      ) : null}
      {(record.is_owner === true || record.privileges?.viewDoctorAttendance === true) && (
        <Tag color="cyan">Doctor attendance</Tag>
      )}
      {(record.is_owner === true || record.privileges?.manageInsurance === true) && (
        <Tag color="geekblue">Insurance</Tag>
      )}
      {record.privileges?.viewerOnly === true && (
        <Tag color="gold" icon={<EyeOutlined />}>Viewer only</Tag>
      )}
      {record.auth_uid && !record.disabled && <Tag color="green" icon={<LockOutlined />}>Login active</Tag>}
      {record.auth_uid && record.disabled && <Tag color="red" icon={<StopOutlined />}>Login disabled</Tag>}
    </Space>
  );

  const columns = [
    {
      title: 'Doctor',
      key: 'doctor',
      width: isMobile ? 176 : undefined,
      render: (record: DoctorRecord) => (
        <Space align="start">
          <Avatar
            icon={<UserOutlined />}
            style={{
              backgroundColor: record.is_owner ? '#722ed1' : record.disabled ? '#bbb' : '#1890ff',
              opacity: record.disabled ? 0.6 : 1,
            }}
          />
          <div>
            <div style={{ fontWeight: 600, color: record.disabled ? '#aaa' : undefined }}>
              {record.name || '—'}
              {record.disabled && <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>Disabled</Tag>}
            </div>
            {record.specialization && (
              <Text type="secondary" style={{ fontSize: 12 }}>{record.specialization}</Text>
            )}
            {isMobile && (
              <div className="owner-doctors-mobile-details">
                {record.email
                  ? <Text className="owner-doctors-mobile-email">{record.email}</Text>
                  : <Tag color="warning">No dashboard login</Tag>}
                {renderDoctorStatus(record)}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['md' as const],
      render: (email: string) => email
        ? <Text style={{ fontSize: 13 }}>{email}</Text>
        : <Tag color="warning">No dashboard login</Tag>,
    },
    {
      title: 'Role',
      key: 'role',
      responsive: ['md' as const],
      render: (record: DoctorRecord) => renderDoctorStatus(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? 132 : 220,
      render: (record: DoctorRecord) => {
        if (record.id === ownerDoctorId) return <Tag color="gold">You (Owner)</Tag>;
        const isLoading = actionLoading === record.id;
        return (
          <Space size={4} wrap>
            <Tooltip title={renderActionHint(doctorActionHints.editTitle, doctorActionHints.editDescription)}>
              <Button aria-label={doctorActionHints.editTitle} size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} disabled={isLoading} />
            </Tooltip>

            <Tooltip title={renderActionHint(doctorActionHints.servicesTitle, doctorActionHints.servicesDescription)}>
              <Button aria-label={doctorActionHints.servicesTitle} size="small" icon={<EyeOutlined />} onClick={() => openDoctorServicesModal(record)} disabled={isLoading} style={{ color: '#722ed1', borderColor: '#722ed1' }} />
            </Tooltip>

            <Tooltip title={renderActionHint(doctorActionHints.privilegesTitle, doctorActionHints.privilegesDescription)}>
              <Button
                aria-label={doctorActionHints.privilegesTitle}
                size="small"
                icon={<SafetyCertificateOutlined />}
                onClick={() => openPrivilegesModal(record)}
                disabled={isLoading}
                style={{ color: '#52c41a', borderColor: '#52c41a' }}
              />
            </Tooltip>

            <Tooltip title={record.email
              ? renderActionHint(doctorActionHints.passwordTitle, doctorActionHints.passwordDescription)
              : renderActionHint(doctorActionHints.noEmailTitle, doctorActionHints.noEmailDescription)}>
              <Button
                aria-label={record.email ? doctorActionHints.passwordTitle : doctorActionHints.noEmailTitle}
                size="small"
                icon={<KeyOutlined />}
                onClick={() => openChangePasswordModal(record)}
                loading={changePasswordLoadingDoctorId === record.id}
                disabled={isLoading || !record.email}
                style={{ color: '#faad14', borderColor: '#faad14' }}
              />
            </Tooltip>

            {!record.auth_uid && (
              <Tooltip title={renderActionHint(doctorActionHints.linkTitle, doctorActionHints.linkDescription)}>
                <Button
                  aria-label={doctorActionHints.linkTitle}
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => { setLinkLoginDoctor(record); setLinkLoginModalVisible(true); }}
                  disabled={isLoading}
                  style={{ color: '#13c2c2', borderColor: '#13c2c2' }}
                />
              </Tooltip>
            )}

            <Tooltip title={record.disabled
              ? renderActionHint(doctorActionHints.enableTitle, doctorActionHints.enableDescription)
              : renderActionHint(doctorActionHints.disableTitle, doctorActionHints.disableDescription)}>
              <Button
                aria-label={record.disabled ? doctorActionHints.enableTitle : doctorActionHints.disableTitle}
                size="small"
                icon={record.disabled ? <UnlockOutlined /> : <StopOutlined />}
                onClick={() => handleToggleDisable(record)}
                loading={isLoading}
                danger={!record.disabled}
                type={record.disabled ? 'default' : 'text'}
                style={record.disabled ? { borderColor: '#52c41a', color: '#52c41a' } : {}}
              />
            </Tooltip>

            <Tooltip title={renderActionHint(doctorActionHints.deleteTitle, doctorActionHints.deleteDescription)}>
              <Popconfirm
                title="Remove doctor permanently?"
                description={
                  <span>
                    This will detach <strong>{record.name}</strong> from your clinic,&nbsp;
                    delete all assistant links for them at this clinic, delete their Firebase login,
                    and remove their doctor profile.
                    This cannot be undone.
                  </span>
                }
                icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                okText="Remove permanently" okType="danger" cancelText="Cancel"
                onConfirm={() => handleRemoveFromClinic(record)}
                disabled={isLoading}
              >
                <Button aria-label={doctorActionHints.deleteTitle} size="small" icon={<DeleteOutlined />} danger type="text" loading={isLoading} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const assistantColumns = [
    {
      title: 'Assistant',
      key: 'assistant',
      width: isMobile ? 176 : undefined,
      render: (record: AssistantRecord) => (
        <Space align="start">
          <Avatar
            icon={<UserOutlined />}
            style={{ backgroundColor: record.disabled ? '#bbb' : '#1677ff' }}
          />
          <div>
            <div style={{ fontWeight: 600, color: record.disabled ? '#aaa' : undefined }}>
              {record.name || 'Unnamed assistant'}
            </div>
            {record.disabled && <Tag color="red">Disabled</Tag>}
            <Tag color={record.canViewDailyRevenue ? 'green' : 'default'}>
              {record.canViewDailyRevenue ? assistantPrivilegesText.visible : assistantPrivilegesText.hidden}
            </Tag>
            {isMobile && (
              <div className="owner-doctors-mobile-details">
                <Text className="owner-doctors-mobile-email">{record.email || '—'}</Text>
                {record.disabled
                  ? <Tag color="red" icon={<StopOutlined />}>Login disabled</Tag>
                  : <Tag color="green" icon={<LockOutlined />}>Login active</Tag>}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['md' as const],
      render: (email: string) => <Text style={{ fontSize: 13 }}>{email || '—'}</Text>,
    },
    {
      title: 'Login',
      key: 'login',
      responsive: ['md' as const],
      render: (record: AssistantRecord) => record.disabled
        ? <Tag color="red" icon={<StopOutlined />}>Login disabled</Tag>
        : <Tag color="green" icon={<LockOutlined />}>Login active</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? 180 : 310,
      render: (record: AssistantRecord) => {
        const isLoading = assistantActionLoading === record.id;
        return (
          <Space size={4} wrap>
            <Tooltip title="Edit assistant name">
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={isLoading}
                onClick={() => {
                  setEditingAssistant(record);
                  editAssistantForm.setFieldsValue({
                    name: record.name,
                  });
                  setEditAssistantModalVisible(true);
                }}
              />
            </Tooltip>
            <Tooltip
              title={(
                <div>
                  <strong>{assistantPrivilegesText.tooltipTitle}</strong>
                  <div>{assistantPrivilegesText.tooltipDescription}</div>
                </div>
              )}
            >
              <Button
                size="small"
                icon={<SafetyCertificateOutlined />}
                disabled={isLoading}
                onClick={() => openAssistantPrivilegesModal(record)}
                style={{ color: '#722ed1', borderColor: '#722ed1' }}
              >
                {assistantPrivilegesText.button}
              </Button>
            </Tooltip>
            <Tooltip title="Change assistant password">
              <Button
                size="small"
                icon={<KeyOutlined />}
                disabled={isLoading}
                onClick={() => {
                  setPasswordAssistant(record);
                  assistantPasswordForm.resetFields();
                  setAssistantPasswordModalVisible(true);
                }}
                style={{ color: '#faad14', borderColor: '#faad14' }}
              />
            </Tooltip>
            <Tooltip title={record.disabled ? 'Enable assistant login' : 'Disable assistant login'}>
              <Button
                size="small"
                icon={record.disabled ? <UnlockOutlined /> : <StopOutlined />}
                loading={isLoading}
                disabled={isLoading}
                danger={!record.disabled}
                onClick={() => handleToggleAssistantDisabled(record)}
              />
            </Tooltip>
            <Tooltip title="Remove assistant from this clinic and delete their account when it is not shared">
              <Popconfirm
                title="Remove assistant?"
                description={`Remove ${record.name} from this clinic? Their login is deleted when it is not linked to another clinic.`}
                okText="Remove"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => handleDeleteAssistant(record)}
                disabled={isLoading}
              >
                <Button size="small" icon={<DeleteOutlined />} danger type="text" loading={isLoading} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // ── Helper: non-owner doctor list ────────────────────────────────
  const nonOwnerDoctors = doctors.filter(d => d.id !== ownerDoctorId);
  const specialtySelectOptions = specialties.map(item => ({
    value: item.id,
    label: `${item.name_ar || ''}${item.name_en ? ` - ${item.name_en}` : ''}`.trim(),
  }));
  if (editingDoctor?.specialization) {
    const normalizedCurrent = editingDoctor.specialization.trim().toLocaleLowerCase();
    const currentExistsInList = specialties.some(item =>
      item.id === editingDoctor.specialty_id
      || [item.name_ar, item.name_en].some(
        name => (name || '').trim().toLocaleLowerCase() === normalizedCurrent,
      )
    );
    if (!currentExistsInList) {
      specialtySelectOptions.unshift({
        value: `${LEGACY_SPECIALTY_PREFIX}${editingDoctor.specialization}`,
        label: `${editingDoctor.specialization} (${language === 'ar' ? 'القيمة الحالية' : 'Current value'})`,
      });
    }
  }

  return (
    <Layout className="owner-doctors-layout">
      {!isMobile && (
        <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      )}
      {isMobile && (
        <Drawer
          className="owner-doctors-mobile-nav"
          placement="right"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          width={260}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          <DashboardSidebar collapsed={false} setCollapsed={() => {}} />
        </Drawer>
      )}
      <Layout className="owner-doctors-main-layout">
        <DashboardHeader
          onSettingsClick={() => navigate('/settings')}
          onMenuClick={() => setMobileNavOpen(true)}
          isMobile={isMobile}
        />
        <Content className="owner-doctors-content">

          {/* ─── Header ──────────────────────────────────────── */}
          <Row className="owner-doctors-page-header" justify="space-between" align="middle">
            <Col className="owner-doctors-heading-column">
              <Space className="owner-doctors-heading" align="center">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} type="text" />
                <TeamOutlined style={{ fontSize: 22, color: '#722ed1' }} />
                <Title className="owner-doctors-title" level={3}>Manage Doctors</Title>
              </Space>
              <Text className="owner-doctors-description" type="secondary">
                New doctors are saved under your clinic owner account in the assistant apps (same pattern as Admin → Relationships), and each doctor can still log in with their own email.
              </Text>
            </Col>
            <Col className="owner-doctors-actions-column">
              <Space className="owner-doctors-page-actions" wrap>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => { loadDoctors(); loadAssistants(); }}
                  loading={loading || assistantsLoading}
                >
                  Refresh
                </Button>

                <Tooltip title="مزامنة أسعار خدماتك مع جميع الأطباء المسجلين">
                  <Button
                    icon={<SyncOutlined />}
                    onClick={handleSyncPrices}
                    loading={syncPricesLoading}
                    style={{ borderColor: '#52c41a', color: '#52c41a' }}
                  >
                    تزامن الأسعار
                  </Button>
                </Tooltip>
                <Button
                  icon={<SafetyCertificateOutlined />}
                  onClick={openAssignInsuranceModal}
                  style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                >
                  إضافة تأمين للأطباء
                </Button>

                <Button
                  icon={<AppstoreAddOutlined />}
                  onClick={openAssignModal}
                  style={{ borderColor: '#13c2c2', color: '#13c2c2' }}
                >
                  إضافة خدمات للأطباء
                </Button>

                <Button
                  icon={<UserAddOutlined />}
                  onClick={() => setAddAssistantModalVisible(true)}
                  style={{ borderColor: '#1677ff', color: '#1677ff' }}
                >
                  Add Assistant
                </Button>

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.resetFields();
                    setAddModalVisible(true);
                  }}
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                >
                  Add Doctor
                </Button>
              </Space>
            </Col>
          </Row>

          {/* ─── Doctors Table ─────────────────────────────────── */}
          <Card className="owner-doctors-list-card">
            <Spin spinning={loading}>
              <Table
                dataSource={doctors}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: 'No doctors linked to this clinic yet.' }}
                size="middle"
                tableLayout={isMobile ? 'fixed' : 'auto'}
                rowClassName={(record) => record.disabled ? 'disabled-doctor-row' : ''}
              />
            </Spin>
          </Card>

          <Card
            title={<Space><TeamOutlined style={{ color: '#1677ff' }} />Assistants</Space>}
            className="owner-doctors-list-card"
            style={{ marginTop: 20 }}
          >
            <Spin spinning={assistantsLoading}>
              <Table
                dataSource={assistants}
                columns={assistantColumns}
                rowKey="id"
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: 'No assistants linked to this clinic yet.' }}
                size="middle"
                tableLayout={isMobile ? 'fixed' : 'auto'}
                rowClassName={(record) => record.disabled ? 'disabled-doctor-row' : ''}
              />
            </Spin>
          </Card>

          {/* ─── Add Doctor Modal ─────────────────────────────── */}
          <Modal
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
                <span>Privileges: {privilegesDoctor?.name}</span>
              </Space>
            }
            open={privilegesModalVisible}
            onOk={handleSavePrivileges}
            onCancel={() => {
              setPrivilegesModalVisible(false);
              setPrivilegesDoctor(null);
              setSelectedPrivilegeKeys([]);
            }}
            okText="Save privileges"
            cancelText="Cancel"
            okButtonProps={{ loading: privilegesSubmitting }}
            width={560}
            destroyOnClose
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Select what this doctor can access in this clinic dashboard.
            </Text>
            <Checkbox.Group
              value={selectedPrivilegeKeys}
              onChange={(values) => setSelectedPrivilegeKeys(values as DoctorPrivilegeKey[])}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {DOCTOR_PRIVILEGES.map(privilege => (
                  <Card key={privilege.key} size="small">
                    <Checkbox
                      value={privilege.key}
                      disabled={privilege.key === 'viewerOnly' && Boolean(
                        privilegesDoctor?.id === ownerDoctorId
                        || isLegacyCompatibleOwner(privilegesDoctor)
                      )}
                    >
                      <Space direction="vertical" size={2}>
                        <Text strong>{privilege.label}</Text>
                        <Text type="secondary">{privilege.description}</Text>
                      </Space>
                    </Checkbox>
                  </Card>
                ))}
              </Space>
            </Checkbox.Group>
          </Modal>

          <Modal
            title={<Space><PlusOutlined />Add New Doctor</Space>}
            open={addModalVisible}
            onCancel={() => { setAddModalVisible(false); form.resetFields(); }}
            afterClose={() => form.resetFields()}
            footer={null} width={480} destroyOnClose
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              The doctor gets their own dashboard login. Your owner account (currently signed in) will also be linked to them so the DrWaiting assistant app shows everyone in this clinic under one parent — like the admin portal screenshot.
            </Text>
            <Divider />
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddDoctor}
              requiredMark="optional"
              autoComplete="off"
              preserve={false}
            >
              <Form.Item name="name" label="Doctor Full Name" rules={[{ required: true, message: 'Please enter the doctor name' }]}>
                <Input prefix={<UserOutlined />} placeholder="e.g. Dr. Ahmed Hassan" />
              </Form.Item>
              <Form.Item name="specialization" label="Specialization (optional)">
                <Input placeholder="e.g. Dentist, Physiotherapist" />
              </Form.Item>
              <Divider orientation="left" style={{ fontSize: 13 }}>Dashboard Login</Divider>
              <Form.Item name="email" label="Email Address" rules={[{ required: true, message: 'Please enter an email' }, { type: 'email', message: 'Enter a valid email address' }]}>
                <Input
                  prefix={<MailOutlined />}
                  placeholder="doctor@example.com"
                  name="new-doctor-email"
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please set a password' }, { min: 6, message: 'Password must be at least 6 characters' }]}>
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Minimum 6 characters"
                  name="new-doctor-password"
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => { setAddModalVisible(false); form.resetFields(); }}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<PlusOutlined />} style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}>
                    Create Doctor
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── Add Assistant Modal ──────────────────────────── */}
          <Modal
            title={<Space><UserAddOutlined />Add New Assistant</Space>}
            open={addAssistantModalVisible}
            onCancel={() => {
              if (addAssistantSubmitting) return;
              setAddAssistantModalVisible(false);
              assistantForm.resetFields();
            }}
            footer={null}
            width={480}
            destroyOnClose
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="The assistant gets their own login and will automatically be linked to every doctor in this clinic."
            />
            <Form
              form={assistantForm}
              layout="vertical"
              onFinish={handleAddAssistant}
              requiredMark="optional"
              initialValues={{ canViewDailyRevenue: false }}
            >
              <Form.Item
                name="name"
                label="Assistant Full Name"
                rules={[{ required: true, whitespace: true, message: 'Please enter the assistant name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="e.g. Sara Ahmed" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Assistant Email"
                rules={[
                  { required: true, message: 'Please enter the assistant email' },
                  { type: 'email', message: 'Enter a valid email address' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="assistant@example.com" autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please set a password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Minimum 6 characters" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm the password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Repeat password" />
              </Form.Item>
              <Form.Item name="canViewDailyRevenue" valuePropName="checked">
                <Checkbox>
                  <strong>Show “تحصيلاتي اليوم”</strong>
                  <div style={{ color: '#6b7280', fontWeight: 400 }}>
                    Allow this assistant to open their daily collections report in the Assistant Portal.
                  </div>
                </Checkbox>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => {
                      setAddAssistantModalVisible(false);
                      assistantForm.resetFields();
                    }}
                    disabled={addAssistantSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={addAssistantSubmitting}
                    icon={<UserAddOutlined />}
                  >
                    Create Assistant
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: '#722ed1' }} />
                <span>{assistantPrivilegesText.modalTitle}: {privilegesAssistant?.name}</span>
              </Space>
            }
            open={assistantPrivilegesModalVisible}
            onOk={handleSaveAssistantPrivileges}
            onCancel={() => {
              if (assistantPrivilegesSubmitting) return;
              setAssistantPrivilegesModalVisible(false);
              setPrivilegesAssistant(null);
            }}
            okText={assistantPrivilegesText.save}
            cancelText={assistantPrivilegesText.cancel}
            okButtonProps={{ loading: assistantPrivilegesSubmitting }}
            cancelButtonProps={{ disabled: assistantPrivilegesSubmitting }}
            closable={!assistantPrivilegesSubmitting}
            maskClosable={!assistantPrivilegesSubmitting}
            width={520}
            destroyOnClose
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {assistantPrivilegesText.modalDescription}
            </Text>
            <Card size="small" style={{ borderColor: assistantCanViewDailyRevenue ? '#b7eb8f' : undefined }}>
              <Checkbox
                checked={assistantCanViewDailyRevenue}
                onChange={event => setAssistantCanViewDailyRevenue(event.target.checked)}
              >
                <Space direction="vertical" size={2}>
                  <Text strong>{assistantPrivilegesText.dailyCollections}</Text>
                  <Text type="secondary">
                    {assistantPrivilegesText.dailyCollectionsDescription}
                  </Text>
                </Space>
              </Checkbox>
            </Card>
          </Modal>

          <Modal
            title={<Space><EditOutlined />Edit Assistant — <strong>{editingAssistant?.name}</strong></Space>}
            open={editAssistantModalVisible}
            onCancel={() => {
              if (editAssistantSubmitting) return;
              setEditAssistantModalVisible(false);
              setEditingAssistant(null);
              editAssistantForm.resetFields();
            }}
            footer={null}
            width={460}
            destroyOnClose
          >
            <Form form={editAssistantForm} layout="vertical" onFinish={handleEditAssistant}>
              <Form.Item
                name="name"
                label="Assistant Full Name"
                rules={[{ required: true, whitespace: true, message: 'Please enter the assistant name' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setEditAssistantModalVisible(false)} disabled={editAssistantSubmitting}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={editAssistantSubmitting} icon={<EditOutlined />}>
                    Save Changes
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title={<Space><KeyOutlined />Change Assistant Password — <strong>{passwordAssistant?.name}</strong></Space>}
            open={assistantPasswordModalVisible}
            onCancel={() => {
              if (assistantPasswordSubmitting) return;
              setAssistantPasswordModalVisible(false);
              setPasswordAssistant(null);
              assistantPasswordForm.resetFields();
            }}
            footer={null}
            width={460}
            destroyOnClose
          >
            <Form form={assistantPasswordForm} layout="vertical" onFinish={handleAssistantPassword}>
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Minimum 6 characters" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm the password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Repeat new password" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setAssistantPasswordModalVisible(false)} disabled={assistantPasswordSubmitting}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={assistantPasswordSubmitting} icon={<KeyOutlined />}>
                    Change Password
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── Edit Doctor Modal ────────────────────────────── */}
          <Modal
            title={<Space><EditOutlined />Edit Doctor — <strong>{editingDoctor?.name}</strong></Space>}
            open={editModalVisible}
            onCancel={() => { setEditModalVisible(false); editForm.resetFields(); setEditingDoctor(null); }}
            footer={null} width={460} destroyOnClose
          >
            {editingDoctor?.email && (
              <Alert
                type="info"
                style={{ marginBottom: 16 }}
                message={
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <MailOutlined />
                      <Text style={{ fontSize: 13 }}>{editingDoctor.email}</Text>
                    </Space>
                    <Tooltip title="Change doctor password directly">
                      <Button
                        size="small"
                        icon={<KeyOutlined />}
                        loading={changePasswordLoadingDoctorId === editingDoctor?.id}
                        onClick={() => editingDoctor && openChangePasswordModal(editingDoctor)}
                        style={{ borderColor: '#faad14', color: '#faad14' }}
                      >
                        Change Password
                      </Button>
                    </Tooltip>
                  </Space>
                }
              />
            )}
            <Form form={editForm} layout="vertical" onFinish={handleEditDoctor} requiredMark="optional">
              <Form.Item name="name" label="Doctor Full Name" rules={[{ required: true, message: 'Please enter the doctor name' }]}>
                <Input prefix={<UserOutlined />} placeholder="e.g. Dr. Ahmed Hassan" />
              </Form.Item>
              <Form.Item
                name="specialtyId"
                label={language === 'ar' ? 'التخصص (اختياري)' : 'Specialization (optional)'}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={specialtiesLoading}
                  options={specialtySelectOptions}
                  placeholder={language === 'ar' ? 'اختر التخصص من القائمة' : 'Select a specialization'}
                  notFoundContent={specialtiesLoading
                    ? <Spin size="small" />
                    : (language === 'ar' ? 'لا توجد تخصصات متاحة' : 'No specialties available')}
                />
              </Form.Item>
              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => { setEditModalVisible(false); editForm.resetFields(); setEditingDoctor(null); }}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={editSubmitting} icon={<EditOutlined />}>Save Changes</Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── Change Password Modal ─────────────────────────── */}
          <Modal
            title={
              <Space>
                <KeyOutlined />
                Change Password — <strong>{passwordDoctor?.name}</strong>
              </Space>
            }
            open={changePasswordModalVisible}
            onCancel={() => {
              if (passwordSubmitting) return;
              setChangePasswordModalVisible(false);
              setPasswordDoctor(null);
              passwordForm.resetFields();
            }}
            footer={null}
            width={460}
            destroyOnClose
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="سيتم تغيير كلمة المرور مباشرة بدون إرسال OTP أو Email."
            />
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePasswordSubmit}
              requiredMark="optional"
            >
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Minimum 6 characters" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm the password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Repeat new password" />
              </Form.Item>

              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => {
                      setChangePasswordModalVisible(false);
                      setPasswordDoctor(null);
                      passwordForm.resetFields();
                    }}
                    disabled={passwordSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={passwordSubmitting}
                    icon={<KeyOutlined />}
                  >
                    Change Password
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── Link Existing Login Modal ───────────────────────── */}
          <Modal
            title={
              <Space>
                <LinkOutlined style={{ color: '#13c2c2' }} />
                Link Dashboard Login — <strong>{linkLoginDoctor?.name}</strong>
              </Space>
            }
            open={linkLoginModalVisible}
            onCancel={() => {
              if (linkLoginSubmitting) return;
              setLinkLoginModalVisible(false);
              setLinkLoginDoctor(null);
              linkLoginForm.resetFields();
            }}
            footer={null}
            width={460}
            destroyOnClose
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Enter the email and password of the existing Firebase account this doctor already uses to log in (e.g. their assistant account). Their account will be linked so the dashboard shows their patients correctly."
            />
            <Form
              form={linkLoginForm}
              layout="vertical"
              onFinish={handleLinkExistingLogin}
              requiredMark="optional"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter the email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="doctor@example.com" autoComplete="off" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter the password' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Account password" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => {
                      setLinkLoginModalVisible(false);
                      setLinkLoginDoctor(null);
                      linkLoginForm.resetFields();
                    }}
                    disabled={linkLoginSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={linkLoginSubmitting}
                    icon={<LinkOutlined />}
                    style={{ background: '#13c2c2', borderColor: '#13c2c2' }}
                  >
                    Link Login
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── View Doctor Services Modal ───────────────────── */}
          <Modal
            title={
              <Space>
                <MedicineBoxOutlined style={{ color: '#722ed1' }} />
                خدمات الدكتور: <strong>{viewingDoctor?.name}</strong>
              </Space>
            }
            open={doctorServicesModalVisible}
            onCancel={() => {
              setDoctorServicesModalVisible(false);
              setViewingDoctor(null);
              setDoctorServicesList([]);
              setServiceActionLoadingId(null);
            }}
            footer={
              <Button onClick={() => {
                setDoctorServicesModalVisible(false);
                setViewingDoctor(null);
                setDoctorServicesList([]);
                setServiceActionLoadingId(null);
              }}
              >
                إغلاق
              </Button>
            }
            width={680}
            destroyOnClose
          >
            {doctorServicesLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
            ) : doctorServicesList.length === 0 ? (
              <Empty description="لا توجد خدمات مضافة لهذا الدكتور حتى الآن." />
            ) : (
              <Table
                dataSource={doctorServicesList}
                rowKey="service_id"
                size="small"
                scroll={isMobile ? { x: 560 } : undefined}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                columns={[
                  {
                    title: 'اسم الخدمة',
                    dataIndex: 'name',
                    key: 'name',
                    render: (t: string) => <strong>{t}</strong>,
                  },
                  {
                    title: 'الفئة',
                    dataIndex: 'category',
                    key: 'category',
                    render: (c: string) => c ? <Tag>{c}</Tag> : '—',
                  },
                  {
                    title: 'السعر',
                    dataIndex: 'price',
                    key: 'price',
                    render: (p: number) => (
                      <Tag color="blue" style={{ fontSize: 13 }}>{p} ج.م</Tag>
                    ),
                  },
                  {
                    title: 'الحالة',
                    dataIndex: 'isActive',
                    key: 'isActive',
                    render: (a: boolean) => a
                      ? <Tag color="success">نشط</Tag>
                      : <Tag color="default">متوقف</Tag>,
                  },
                  {
                    title: 'إجراءات',
                    key: 'actions',
                    width: 120,
                    render: (_: unknown, row: ClinicService) => row.isActive !== false ? (
                      <Popconfirm
                        title="إخفاء الخدمة؟"
                        description={`ستختفي "${row.name}" من الاختيارات الجديدة فقط، ولن تتغير الإيصالات القديمة.`}
                        okText="إخفاء"
                        cancelText="إلغاء"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleSetDoctorServiceActive(row, false)}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<StopOutlined />}
                          loading={serviceActionLoadingId === row.service_id}
                        >
                          إخفاء
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        size="small"
                        icon={<UnlockOutlined />}
                        loading={serviceActionLoadingId === row.service_id}
                        onClick={() => handleSetDoctorServiceActive(row, true)}
                      >
                        استعادة
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Modal>

          {/* ─── Assign Services to Doctors Modal ────────────── */}
          <Modal
            title={
              <Space>
                <AppstoreAddOutlined style={{ color: '#13c2c2' }} />
                <span>إضافة خدمات وأنواع زيارات للأطباء</span>
              </Space>
            }
            open={assignModalVisible}
            onCancel={() => setAssignModalVisible(false)}
            onOk={handleAssignSubmit}
            okText="إضافة للأطباء المحددين"
            cancelText="إلغاء"
            okButtonProps={{ loading: assignSubmitting, style: { backgroundColor: '#13c2c2', borderColor: '#13c2c2' } }}
            width={700} destroyOnClose
          >
            {assignLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
            ) : (
              <div>
                <Alert
                  message="سيتم نسخ الخدمات وأنواع الزيارات من حسابك إلى الأطباء المحددين. الخدمات المكررة تُتجاهل تلقائياً."
                  type="info" showIcon style={{ marginBottom: 16 }}
                />

                {/* ── Doctors ───────────────────────────────── */}
                <Card size="small" style={{ marginBottom: 12 }}
                  title={
                    <Space>
                      <TeamOutlined />
                      <span>الأطباء</span>
                      <Badge count={selectedDoctorIds.length} style={{ backgroundColor: '#13c2c2' }} />
                    </Space>
                  }
                  extra={
                    <Button size="small" type="link" onClick={() => {
                      const ids = nonOwnerDoctors.map(d => d.id);
                      setSelectedDoctorIds(selectedDoctorIds.length === ids.length ? [] : ids);
                    }}>
                      {selectedDoctorIds.length === nonOwnerDoctors.length ? 'إلغاء الكل' : 'تحديد الكل'}
                    </Button>
                  }
                >
                  {nonOwnerDoctors.length === 0 ? (
                    <Text type="secondary">لا يوجد أطباء آخرون في العيادة.</Text>
                  ) : (
                    <Checkbox.Group value={selectedDoctorIds} onChange={vals => setSelectedDoctorIds(vals as string[])} style={{ width: '100%' }}>
                      <Row gutter={[8, 8]}>
                        {nonOwnerDoctors.map(d => (
                          <Col xs={24} sm={12} key={d.id}>
                            <Checkbox value={d.id}>
                              <Space size={4}>
                                <UserOutlined style={{ color: '#1890ff' }} />
                                <span>{d.name}</span>
                                {d.specialization && <Text type="secondary" style={{ fontSize: 11 }}>({d.specialization})</Text>}
                              </Space>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  )}
                </Card>

                {/* ── Services ──────────────────────────────── */}
                <Card size="small" style={{ marginBottom: 12 }}
                  title={
                    <Space>
                      <MedicineBoxOutlined />
                      <span>الخدمات</span>
                      <Badge count={selectedServiceIds.length} style={{ backgroundColor: '#722ed1' }} />
                    </Space>
                  }
                  extra={
                    <Button size="small" type="link" onClick={() =>
                      setSelectedServiceIds(selectedServiceIds.length === ownerServices.length ? [] : ownerServices.map(s => s.service_id))
                    }>
                      {selectedServiceIds.length === ownerServices.length ? 'إلغاء الكل' : 'تحديد الكل'}
                    </Button>
                  }
                >
                  {ownerServices.length === 0 ? (
                    <Text type="secondary">لا توجد خدمات في حسابك. أضف خدمات من إعدادات الخدمات أولاً.</Text>
                  ) : (
                    <Checkbox.Group value={selectedServiceIds} onChange={vals => setSelectedServiceIds(vals as string[])} style={{ width: '100%' }}>
                      <Row gutter={[8, 8]}>
                        {ownerServices.map(svc => (
                          <Col xs={24} sm={12} key={svc.service_id}>
                            <Checkbox value={svc.service_id}>
                              <Space size={4}>
                                <span>{svc.name}</span>
                                <Tag color="blue" style={{ fontSize: 11 }}>{svc.price} ج.م</Tag>
                              </Space>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  )}
                </Card>

                {/* ── Visit Types ───────────────────────────── */}
                <Card size="small"
                  title={
                    <Space>
                      <ScheduleOutlined />
                      <span>أنواع الزيارات</span>
                      <Badge count={selectedVisitTypeIds.length} style={{ backgroundColor: '#52c41a' }} />
                    </Space>
                  }
                  extra={
                    <Button size="small" type="link" onClick={() =>
                      setSelectedVisitTypeIds(selectedVisitTypeIds.length === ownerVisitTypes.length ? [] : ownerVisitTypes.map(vt => vt.type_id))
                    }>
                      {selectedVisitTypeIds.length === ownerVisitTypes.length ? 'إلغاء الكل' : 'تحديد الكل'}
                    </Button>
                  }
                >
                  {ownerVisitTypes.length === 0 ? (
                    <Text type="secondary">لا توجد أنواع زيارات مخصصة. أضفها من إعدادات الخدمات أولاً.</Text>
                  ) : (
                    <Checkbox.Group value={selectedVisitTypeIds} onChange={vals => setSelectedVisitTypeIds(vals as string[])} style={{ width: '100%' }}>
                      <Row gutter={[8, 8]}>
                        {ownerVisitTypes.map(vt => (
                          <Col xs={24} sm={12} key={vt.type_id}>
                            <Checkbox value={vt.type_id}>
                              <Space size={4}>
                                <span>{vt.name_ar || vt.name}</span>
                                <Tag color="green" style={{ fontSize: 11 }}>{vt.normal_price} ج.م</Tag>
                              </Space>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  )}
                </Card>
              </div>
            )}
          </Modal>

          {/* ─── Assign Insurance Companies Modal ────────────── */}
          <Modal
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: '#fa8c16' }} />
                <span>إضافة شركات التأمين للأطباء</span>
              </Space>
            }
            open={assignInsuranceModalVisible}
            onCancel={() => setAssignInsuranceModalVisible(false)}
            onOk={handleAssignInsuranceSubmit}
            okText="إضافة للأطباء المحددين"
            cancelText="إلغاء"
            okButtonProps={{ loading: assignInsuranceSubmitting, style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' } }}
            width={700} destroyOnClose
          >
            {assignInsuranceLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
            ) : (
              <div>
                <Alert
                  message="سيتم نسخ شركات التأمين من حسابك إلى الأطباء المحددين. الشركات المكررة تُتجاهل تلقائياً."
                  type="info" showIcon style={{ marginBottom: 16 }}
                />

                {/* ── Doctors ───────────────────────────────── */}
                <Card size="small" style={{ marginBottom: 12 }}
                  title={
                    <Space>
                      <TeamOutlined />
                      <span>الأطباء</span>
                      <Badge count={selectedInsuranceDoctorIds.length} style={{ backgroundColor: '#fa8c16' }} />
                    </Space>
                  }
                  extra={
                    <Button size="small" type="link" onClick={() => {
                      const ids = nonOwnerDoctors.map(d => d.id);
                      setSelectedInsuranceDoctorIds(selectedInsuranceDoctorIds.length === ids.length ? [] : ids);
                    }}>
                      {selectedInsuranceDoctorIds.length === nonOwnerDoctors.length ? 'إلغاء الكل' : 'تحديد الكل'}
                    </Button>
                  }
                >
                  {nonOwnerDoctors.length === 0 ? (
                    <Text type="secondary">لا يوجد أطباء آخرون في العيادة.</Text>
                  ) : (
                    <Checkbox.Group value={selectedInsuranceDoctorIds} onChange={vals => setSelectedInsuranceDoctorIds(vals as string[])} style={{ width: '100%' }}>
                      <Row gutter={[8, 8]}>
                        {nonOwnerDoctors.map(d => (
                          <Col xs={24} sm={12} key={d.id}>
                            <Checkbox value={d.id}>
                              <Space size={4}>
                                <UserOutlined style={{ color: '#fa8c16' }} />
                                <span>{d.name}</span>
                                {d.specialization && <Text type="secondary" style={{ fontSize: 11 }}>({d.specialization})</Text>}
                              </Space>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  )}
                </Card>

                {/* ── Insurance Companies ───────────────────── */}
                <Card size="small"
                  title={
                    <Space>
                      <SafetyCertificateOutlined />
                      <span>شركات التأمين</span>
                      <Badge count={selectedInsuranceIds.length} style={{ backgroundColor: '#722ed1' }} />
                    </Space>
                  }
                  extra={
                    <Button size="small" type="link" onClick={() =>
                      setSelectedInsuranceIds(selectedInsuranceIds.length === ownerInsurance.length ? [] : ownerInsurance.map(c => c.company_id))
                    }>
                      {selectedInsuranceIds.length === ownerInsurance.length ? 'إلغاء الكل' : 'تحديد الكل'}
                    </Button>
                  }
                >
                  {ownerInsurance.length === 0 ? (
                    <Text type="secondary">لا توجد شركات تأمين في حسابك. أضفها من صفحة إدارة شركات التأمين أولاً.</Text>
                  ) : (
                    <Checkbox.Group value={selectedInsuranceIds} onChange={vals => setSelectedInsuranceIds(vals as string[])} style={{ width: '100%' }}>
                      <Row gutter={[8, 8]}>
                        {ownerInsurance.map(comp => (
                          <Col xs={24} sm={12} key={comp.company_id}>
                            <Checkbox value={comp.company_id}>
                              <Space size={4}>
                                <span>{comp.name_ar}</span>
                                <Tag color="orange" style={{ fontSize: 11 }}>
                                  {comp.coverage_percentage}% تغطية
                                </Tag>
                              </Space>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  )}
                </Card>
              </div>
            )}
          </Modal>

        </Content>
      </Layout>
    </Layout>
  );
};

export default OwnerDoctorManagement;
